// ============================================================
// Portal theme from the agency's branding (primary + accent color).
// Color math is pure (tested); createPortalTheme wraps it for MUI.
// ============================================================

import { createTheme, type Theme } from '@mui/material/styles'

import { theme as dashboardTheme } from '@/app/theme'
import { DEFAULT_PORTAL_BRANDING, type PortalBranding } from '@/apis/portalTypes'

const HEX_RE = /^#[0-9a-f]{6}$/i

export function normalizeHex(v: string | null | undefined, fallback: string): string {
    return v && HEX_RE.test(v) ? v.toLowerCase() : fallback
}

function channels(hex: string): [number, number, number] {
    const n = parseInt(hex.slice(1), 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function toHex([r, g, b]: [number, number, number]): string {
    return `#${[r, g, b]
        .map(c =>
            Math.max(0, Math.min(255, Math.round(c)))
                .toString(16)
                .padStart(2, '0'),
        )
        .join('')}`
}

/** WCAG relative luminance (0 = black, 1 = white). */
export function luminance(hex: string): number {
    const [r, g, b] = channels(hex).map(c => {
        const s = c / 255
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
    })
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Text color (white or near-black) with the better contrast on `hex`. */
export function contrastText(hex: string): string {
    const l = luminance(hex)
    const vsWhite = 1.05 / (l + 0.05)
    const vsDark = (l + 0.05) / 0.05
    return vsWhite >= vsDark ? '#ffffff' : '#111827'
}

/** Mix toward white (amount > 0) or black (amount < 0), -1…1. */
export function shade(hex: string, amount: number): string {
    const target = amount >= 0 ? 255 : 0
    const a = Math.abs(amount)
    return toHex(channels(hex).map(c => c + (target - c) * a) as [number, number, number])
}

/** '#rrggbb' + alpha 0…1 → '#rrggbbaa'. */
export function withAlpha(hex: string, alpha: number): string {
    const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
    return `${normalizeHex(hex, '#000000')}${a.toString(16).padStart(2, '0')}`
}

export interface PortalPalette {
    primary: { main: string; light: string; dark: string; contrastText: string }
    secondary: { main: string; light: string; dark: string; contrastText: string }
}

export function brandingPalette(
    branding: Partial<PortalBranding> | null | undefined,
): PortalPalette {
    const p = normalizeHex(branding?.primaryColor, DEFAULT_PORTAL_BRANDING.primaryColor)
    const a = normalizeHex(branding?.accentColor, DEFAULT_PORTAL_BRANDING.accentColor)
    return {
        primary: {
            main: p,
            light: shade(p, 0.25),
            dark: shade(p, -0.2),
            contrastText: contrastText(p),
        },
        secondary: {
            main: a,
            light: shade(a, 0.25),
            dark: shade(a, -0.2),
            contrastText: contrastText(a),
        },
    }
}

/** The dashboard theme with the portal's brand colors. */
export function createPortalTheme(branding: Partial<PortalBranding> | null | undefined): Theme {
    const palette = brandingPalette(branding)
    return createTheme({
        palette: {
            mode: 'light',
            primary: palette.primary,
            secondary: palette.secondary,
            background: dashboardTheme.palette.background,
            text: {
                primary: dashboardTheme.palette.text.primary,
                secondary: dashboardTheme.palette.text.secondary,
            },
        },
        typography: {
            fontFamily: dashboardTheme.typography.fontFamily,
            h4: { fontWeight: 700 },
            h5: { fontWeight: 600 },
            h6: { fontWeight: 600 },
        },
        shape: { borderRadius: 8 },
        components: dashboardTheme.components,
    })
}
